import './styles.css'
import { h } from './charts/svg.ts'
import { currentSession, logout } from './sdk/auth.ts'
import {
  controllerName,
  listInterfaces,
  listOrganizations,
  matchesSearch,
  sortControllers,
  stateLabel,
  type ControllerInterface,
} from './sdk/interfaces.ts'
import { fetchAttributes, type Attribute } from './sdk/attributes.ts'
import { fetchFlow, type FetchProgress } from './report/fetchFlow.ts'
import { renderLogin } from './ui/login.ts'
import { defaultBucket, renderFilters, windowFor, type FilterState } from './ui/filters.ts'
import { renderFlowReport } from './ui/report.ts'
import { createBuilderState, renderBuilder, type BuilderState } from './ui/builder.ts'
import type { Bucket, Window } from './lib/time.ts'
import { RpcError } from './sdk/rpc.ts'
import { getUnitSystem, setUnitSystem, UNIT_SYSTEMS } from './lib/units.ts'
import type { FetchedFlow } from './report/fetchFlow.ts'

const root = document.getElementById('app')!

interface State {
  interfaces: ControllerInterface[]
  orgs: Map<number, string>
  attributes: Attribute[]
  selectedId: number | null
  filters: FilterState
  devMode: boolean
  builder: BuilderState
  search: string
  /** The last successful fetch, so a display-only change re-renders without a refetch. */
  lastReport: { fetched: FetchedFlow; window: Window; bucket: Bucket; controllerId: number } | null
}

const state: State = {
  interfaces: [],
  orgs: new Map(),
  attributes: [],
  selectedId: null,
  filters: { range: { kind: 'preset', days: 7 }, bucket: 'day', serverPeriod: true },
  devMode: true,
  builder: createBuilderState(),
  search: '',
  lastReport: null,
}

// --- theme -----------------------------------------------------------------

const THEME_KEY = 'ir-theme'
function applyTheme(theme: string | null) {
  if (theme === 'dark' || theme === 'light') document.documentElement.setAttribute('data-theme', theme)
  else document.documentElement.removeAttribute('data-theme')
}
function currentTheme(): string | null {
  try {
    return localStorage.getItem(THEME_KEY)
  } catch {
    return null
  }
}
applyTheme(currentTheme())

function toggleTheme() {
  const now = document.documentElement.getAttribute('data-theme')
  const next = now === 'dark' ? 'light' : now === 'light' ? null : 'dark'
  applyTheme(next)
  try {
    if (next) localStorage.setItem(THEME_KEY, next)
    else localStorage.removeItem(THEME_KEY)
  } catch {
    /* private mode — the toggle still works for this page view */
  }
}

// --- environment -------------------------------------------------------------

const PRODUCTION_BRANCH = 'main'

/** A pill naming the branch on anything that is not the production build. */
function envBadge(): HTMLElement | null {
  if (__BUILD_BRANCH__ === PRODUCTION_BRANCH) return null
  const label = __BUILD_BRANCH__ === 'local' ? 'local' : `test · ${__BUILD_BRANCH__}`
  return h('span', { class: 'pill pill-env', title: 'Not the production deployment', text: label })
}

// --- boot ------------------------------------------------------------------

async function boot() {
  root.replaceChildren(
    h('div', { class: 'login-wrap' }, h('div', { class: 'progress' }, h('div', { class: 'spinner' }), h('span', { text: 'Checking session…' }))),
  )
  try {
    // users.admin is the "am I still logged in?" probe: a result means an
    // existing session, an error means show the form.
    await currentSession()
    await loadAccount()
  } catch {
    root.replaceChildren(renderLogin(() => void afterLogin()))
  }
}

async function afterLogin() {
  try {
    await loadAccount()
  } catch (err) {
    root.replaceChildren(fatal(err))
  }
}

async function loadAccount() {
  const [interfaces, orgs, attributes] = await Promise.all([
    listInterfaces(),
    listOrganizations().catch(() => []),
    fetchAttributes(),
  ])
  state.interfaces = interfaces
  state.orgs = new Map(orgs.map((o) => [o.id, o.name]))
  state.attributes = attributes
  // Only ever offer what interface.list returned — that list is the
  // authorisation boundary.
  state.selectedId = interfaces[0]?.id ?? null
  state.filters.bucket = defaultBucket(state.filters.range)
  render()
  if (state.selectedId !== null) void loadReport()
}

function fatal(err: unknown): HTMLElement {
  return h(
    'div',
    { class: 'login-wrap' },
    h(
      'div',
      { class: 'card login-card' },
      h('h1', { text: 'Something went wrong' }),
      h('p', { class: 'secondary', text: err instanceof Error ? err.message : String(err) }),
      (() => {
        const b = h('button', { class: 'btn btn-primary', type: 'button', text: 'Reload' })
        b.addEventListener('click', () => location.reload())
        return b
      })(),
    ),
  )
}

// --- shell -----------------------------------------------------------------

const reportSlot = h('div', { style: 'display:grid;gap:20px' })
const builderSlot = h('div', {})

function selected(): ControllerInterface | null {
  return state.interfaces.find((i) => i.id === state.selectedId) ?? null
}

const sidebarList = h('div', { style: 'display:grid;gap:14px' })

function renderSidebar(): HTMLElement {
  const search = h('input', {
    type: 'search',
    id: 'ctrl-search',
    placeholder: 'Search controllers…',
    autocomplete: 'off',
    value: state.search,
    'aria-label': 'Search controllers',
  })
  search.addEventListener('input', () => {
    state.search = search.value
    renderControllerList()
  })
  // Esc clears the search without leaving the field.
  search.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && search.value) {
      search.value = ''
      state.search = ''
      renderControllerList()
    }
  })

  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  icon.setAttribute('viewBox', '0 0 16 16')
  icon.setAttribute('class', 'search-icon')
  icon.innerHTML =
    '<circle cx="6.5" cy="6.5" r="4.5" fill="none" stroke="currentColor" stroke-width="1.6"/>' +
    '<path d="M10 10l4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>'

  renderControllerList()
  return h('aside', { class: 'sidebar' }, h('div', { class: 'search' }, icon, search), sidebarList)
}

function renderControllerList() {
  const query = state.search.trim()
  const visible = state.interfaces.filter((i) => matchesSearch(i, state.orgs.get(i.organization_id), query))

  const byOrg = new Map<number, ControllerInterface[]>()
  for (const i of visible) {
    if (!byOrg.has(i.organization_id)) byOrg.set(i.organization_id, [])
    byOrg.get(i.organization_id)!.push(i)
  }
  const groups = [...byOrg.entries()].sort((a, b) =>
    (state.orgs.get(a[0]) ?? String(a[0])).localeCompare(state.orgs.get(b[0]) ?? String(b[0])),
  )

  const count = query
    ? `${visible.length} of ${state.interfaces.length} controllers`
    : `${state.interfaces.length} controller${state.interfaces.length === 1 ? '' : 's'}`

  sidebarList.replaceChildren(
    h('div', { class: 'org-name', text: count }),
    ...(visible.length
      ? groups.map(([orgId, list]) =>
          h(
            'div',
            { class: 'org-group' },
            h('div', { class: 'org-name', text: state.orgs.get(orgId) ?? `Organisation ${orgId}` }),
            ...sortControllers(list).map((i) => {
              const st = stateLabel(i.state)
              const meta = [i.product_id || 'controller', `#${i.id}`]
              if (i.log_subscribed === false) meta.push('no log subscription')
              const btn = h(
                'button',
                { class: 'ctrl-item', type: 'button' },
                h('span', { class: 'ctrl-name', text: controllerName(i) }),
                h('span', { class: `dot dot-${st.tone}`, title: st.text }),
                h('span', { class: 'ctrl-meta', text: meta.join(' · ') }),
              )
              btn.setAttribute('aria-current', String(i.id === state.selectedId))
              btn.addEventListener('click', () => {
                if (i.id === state.selectedId) return
                state.selectedId = i.id
                state.builder = createBuilderState()
                render()
                void loadReport()
              })
              return btn
            }),
          ),
        )
      : [h('div', { class: 'empty', text: `Nothing matches “${query}”.` })]),
  )
}

function render() {
  const ctrl = selected()
  const themeBtn = h('button', { class: 'btn btn-ghost btn-sm', type: 'button', text: 'Theme' })
  themeBtn.addEventListener('click', toggleTheme)

  const devBtn = h('button', { class: 'btn btn-sm', type: 'button', text: state.devMode ? 'Hide source data' : 'Show source data' })
  devBtn.setAttribute('aria-pressed', String(state.devMode))
  devBtn.addEventListener('click', () => {
    state.devMode = !state.devMode
    render()
    rerenderReport()
  })

  const unitsCtl = h(
    'div',
    { class: 'segmented', role: 'group', 'aria-label': 'Units' },
    ...UNIT_SYSTEMS.map((u) => {
      const b = h('button', { class: 'btn btn-sm', type: 'button', text: u.label, title: u.title })
      b.setAttribute('aria-pressed', String(getUnitSystem() === u.id))
      b.addEventListener('click', () => {
        if (getUnitSystem() === u.id) return
        setUnitSystem(u.id)
        render()
        rerenderReport()
      })
      return b
    }),
  )

  const outBtn = h('button', { class: 'btn btn-ghost btn-sm', type: 'button', text: 'Sign out' })
  outBtn.addEventListener('click', async () => {
    await logout()
    location.reload()
  })

  root.replaceChildren(
    h(
      'div',
      { class: 'app' },
      h(
        'header',
        { class: 'topbar' },
        h('div', { class: 'brand-mark' }),
        h('h1', { style: 'font-size:16px', text: 'Irrigation reports' }),
        ctrl ? h('span', { class: 'pill', text: controllerName(ctrl) }) : null,
        envBadge(),
        h('div', { class: 'spacer' }),
        unitsCtl,
        devBtn,
        themeBtn,
        outBtn,
      ),
      h(
        'div',
        { class: 'layout' },
        renderSidebar(),
        h(
          'main',
          { class: 'main' },
          renderFilters(state.filters, (next) => {
            state.filters = next
            render()
            void loadReport()
          }),
          reportSlot,
          builderSlot,
        ),
      ),
    ),
  )
  renderBuilderSlot()
}

function renderBuilderSlot() {
  const ctrl = selected()
  if (!ctrl) {
    builderSlot.replaceChildren()
    return
  }
  builderSlot.replaceChildren(
    renderBuilder(state.builder, {
      attributes: state.attributes,
      interfaceId: ctrl.id,
      window: windowFor(state.filters.range),
      bucket: state.filters.bucket,
      serverPeriod: state.filters.serverPeriod,
      devMode: state.devMode,
      onChange: renderBuilderSlot,
    }),
  )
}

// --- report ----------------------------------------------------------------

let reportToken = 0

/** Redraw from the last fetch — for changes that alter presentation only. */
function rerenderReport() {
  const last = state.lastReport
  if (!last || last.controllerId !== state.selectedId) return
  reportSlot.replaceChildren(renderFlowReport(last.fetched, last.window, last.bucket, state.devMode))
}

async function loadReport() {
  const ctrl = selected()
  if (!ctrl) return
  const token = ++reportToken
  const label = h('span', { text: 'Fetching flow volume…' })
  reportSlot.replaceChildren(
    h('div', { class: 'card report-card' }, h('div', { class: 'progress' }, h('div', { class: 'spinner' }), label)),
  )

  const window = windowFor(state.filters.range)
  const bucket: Bucket = state.filters.bucket
  try {
    const fetched = await fetchFlow(ctrl.id, window, bucket, state.attributes, {
      serverPeriod: state.filters.serverPeriod,
      onProgress: (p: FetchProgress) => {
        if (token !== reportToken) return
        label.textContent = `${p.label} — ${p.rows.toLocaleString('en-GB')} rows (${p.done + 1}/${p.total})`
      },
    })
    if (token !== reportToken) return // a newer request already superseded this one
    state.lastReport = { fetched, window, bucket, controllerId: ctrl.id }
    reportSlot.replaceChildren(renderFlowReport(fetched, window, bucket, state.devMode))
  } catch (err) {
    if (token !== reportToken) return
    // A 401 that survived the one-shot refresh means the session is gone:
    // back to the form, not an error banner over a stale page.
    if (err instanceof RpcError && err.isAuth) {
      root.replaceChildren(renderLogin(() => void afterLogin()))
      return
    }
    reportSlot.replaceChildren(
      h(
        'div',
        { class: 'banner banner-critical' },
        h('span', { class: 'banner-icon', text: '!' }),
        h('span', { text: err instanceof Error ? err.message : String(err) }),
      ),
    )
  }
}

// `?mock=1` swaps in a local fake cloud so the page can be developed and
// reviewed without an account. Code-split: a normal load never fetches it.
if (new URLSearchParams(location.search).has('mock')) {
  const { installMock } = await import('./dev/mock.ts')
  installMock()
}

void boot()
